import React, {
  useState,
} from "react";

import {
  ToastContainer,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../hooks/useScollToTop";

import {
  useNotification,
} from "../../../components/Notification/NotificationManager";

import ActeursDesktopTable from "./components/ActeursDesktopTable";
import ActeursFiltersBar from "./components/ActeursFiltersBar";
import ActeursHeader from "./components/ActeursHeader";
import ActeursMobileCards from "./components/ActeursMobileCards";
import ActeursPagination from "./components/ActeursPagination";
import CreateActeurModal from "./components/CreateActeurModal";
import EditActeurModal from "./components/EditActeurModal";
import ManageGroupModal from "./components/ManageGroupModal";

import useActeursAdmin from "./hooks/useActeursAdmin";

export default function ActeursPage() {
  useScrollToTop();

  const {
    addNotification,
  } = useNotification();

  const {
    groups,
    users,

    stats,
    filteredActeurs,
    paginatedActeurs,

    searchTerm,
    setSearchTerm,
    debouncedSearch,

    selectedGroupId,
    setSelectedGroupId,

    linkFilter,
    setLinkFilter,

    page,
    setPage,
    pageCount,

    isLoading,
    isDeleting,

    refreshActeurs,
    removeActeur,
    resetFilters,
  } = useActeursAdmin(
    addNotification,
  );

  /*
   * Modale de création
   */
  const [
    showCreateModal,
    setShowCreateModal,
  ] = useState(false);

  /*
   * On conserve l'objet acteur complet
   * pour avoir accès à acteur.Users
   * dans la modale d'édition.
   */
  const [
    editActeur,
    setEditActeur,
  ] = useState(null);

  /*
   * Acteur sélectionné pour la gestion
   * de ses groupes.
   */
  const [
    manageGroupActeur,
    setManageGroupActeur,
  ] = useState(null);

  const selectedGroupLabel =
    groups.find(
      (group) =>
        String(group.id) ===
        String(selectedGroupId),
    )?.nom;

  const openCreateModal = () => {
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
  };

  const openEditModal = (acteur) => {
    setEditActeur(acteur);
  };

  const closeEditModal = () => {
    setEditActeur(null);
  };

  const openManageGroupModal = (
    acteur,
  ) => {
    setManageGroupActeur(acteur);
  };

  const closeManageGroupModal = () => {
    setManageGroupActeur(null);
  };

  return (
    <div className="w-full pb-10 pt-[96px] font-satoshi">
      <div className="mx-auto w-full max-w-[1400px] px-4">
        {/* En-tête et statistiques */}
        <ActeursHeader
          stats={stats}
        />

        {/* Filtres */}
        <div className="mt-5">
          <ActeursFiltersBar
            searchTerm={searchTerm}
            setSearchTerm={
              setSearchTerm
            }
            selectedGroupId={
              selectedGroupId
            }
            setSelectedGroupId={
              setSelectedGroupId
            }
            linkFilter={linkFilter}
            setLinkFilter={
              setLinkFilter
            }
            groups={groups}
            onReset={resetFilters}
            onCreate={
              openCreateModal
            }
            loading={isLoading}
          />
        </div>

        {/* Résumé des filtres actifs */}
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {filteredActeurs.length}{" "}
            acteur
            {filteredActeurs.length > 1
              ? "s"
              : ""}
          </span>

          {selectedGroupId ? (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Groupe :{" "}
              {selectedGroupLabel ||
                selectedGroupId}
            </span>
          ) : null}

          {linkFilter !== "all" ? (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              {linkFilter === "linked"
                ? "Avec compte utilisateur"
                : "Sans compte utilisateur"}
            </span>
          ) : null}

          {debouncedSearch ? (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Recherche :{" "}
              {searchTerm}
            </span>
          ) : null}

          {!isLoading &&
          filteredActeurs.length > 0 ? (
            <span className="ml-auto text-xs">
              Affichage{" "}
              {
                paginatedActeurs.length
              }{" "}
              • page {page} /{" "}
              {pageCount}
            </span>
          ) : null}
        </div>

        {/* Tableau desktop et cartes mobiles */}
        <div className="mt-5">
          <ActeursDesktopTable
            rows={
              paginatedActeurs
            }
            loading={isLoading}
            deleting={isDeleting}
            onEdit={
              openEditModal
            }
            onDelete={
              removeActeur
            }
            onManageGroups={
              openManageGroupModal
            }
            onCreate={
              openCreateModal
            }
          />

          <ActeursMobileCards
            rows={
              paginatedActeurs
            }
            loading={isLoading}
            deleting={isDeleting}
            onEdit={
              openEditModal
            }
            onDelete={
              removeActeur
            }
            onManageGroups={
              openManageGroupModal
            }
            onCreate={
              openCreateModal
            }
          />
        </div>

        {/* Pagination */}
        <ActeursPagination
          page={page}
          pageCount={pageCount}
          setPage={setPage}
          loading={isLoading}
        />
      </div>

      {/* Création */}
      <CreateActeurModal
        isOpen={showCreateModal}
        onClose={
          closeCreateModal
        }
        refreshList={
          refreshActeurs
        }
        users={users}
      />

      {/* Édition et association d'un utilisateur */}
      <EditActeurModal
        isOpen={!!editActeur}
        acteur={editActeur}
        onClose={
          closeEditModal
        }
        refreshList={
          refreshActeurs
        }
        users={users}
      />

      {/* Gestion des groupes */}
      <ManageGroupModal
        isOpen={
          !!manageGroupActeur
        }
        onClose={
          closeManageGroupModal
        }
        acteurId={
          manageGroupActeur?.id
        }
        acteurNom={
          manageGroupActeur?.nom
        }
        acteurPrenom={
          manageGroupActeur?.prenom
        }
        groups={groups}
        acteurGroups={
          manageGroupActeur?.Groups ||
          []
        }
        refreshData={
          refreshActeurs
        }
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
      />
    </div>
  );
}